/**
 * ImportClientsWizard - Multi-step wizard for bulk client import.
 *
 * Steps:
 *  1. Upload - Select a file (xlsx, csv, json, etc.) + download template
 *  2. Parsing - AI parses & classifies (PubSub progress)
 *  3. Preview - Review & edit parsed rows (type, lawyers, etc.)
 *  4. Confirm - Execute client creation with progress
 *  5. Documents - Optional ZIP/file upload for client documents
 *  6. Results - Summary
 *
 * Chrome (dialog, step rail, error banner, footer) comes from
 * `ImportWizardShell`, shared with the users wizard.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiUploadLight,
  PiUploadSimpleLight,
  PiSparkleLight,
  PiTableLight,
  PiUserPlusLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiDownloadLight,
  PiUsersFourLight,
  PiFilesLight,
  PiArrowRightLight,
  PiFolderOpenLight,
  PiFolderLight,
  PiFileLight,
} from 'react-icons/pi';

import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import Badge from '../shared/TailwindComponents/Badge';
import Alert from '../shared/TailwindComponents/Alert';
import { Well } from '../shared/brand/Field';
import { FIELD_LABEL_CLASS } from '../shared/TailwindComponents/FieldShell';
import type { WizardStepRailItem } from '../shared/FormComponents/WizardStepRail';
import ImportWizardShell, {
  ImportProcessingState,
  ImportResultHeader,
  ImportStatStrip,
  ImportFailureList,
  ImportSelectionToggles,
  ImportRowActions,
  IMPORT_TH_CLASS,
  importRowClass,
  type ImportStat,
} from './ImportWizardShell';
import { useImportPubSub } from '../../hooks/useImportPubSub';
import {
  uploadClientsImport,
  getImportStatus,
  updateImportPreview,
  confirmImport,
  uploadImportDocuments,
  skipDocumentUpload,
  downloadClientTemplate,
} from '../../services/import/importApi';
import type {
  ImportWizardStep,
  ImportClientRow,
  ImportPreviewData,
  ImportConfirmResponse,
  ImportDocumentsResponse,
} from '../../types/import';
import { readErrorMessage, toErrorText } from '../../utils/errorMessage';

interface ImportClientsWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const STEPS: WizardStepRailItem<ImportWizardStep>[] = [
  { key: 'upload', label: 'Upload', icon: <PiUploadSimpleLight className="w-4 h-4" /> },
  { key: 'parsing', label: 'AI Parsing', icon: <PiSparkleLight className="w-4 h-4" /> },
  { key: 'preview', label: 'Preview', icon: <PiTableLight className="w-4 h-4" /> },
  { key: 'confirm', label: 'Creating', icon: <PiUserPlusLight className="w-4 h-4" /> },
  { key: 'documents', label: 'Documents', icon: <PiFilesLight className="w-4 h-4" /> },
  { key: 'results', label: 'Results', icon: <PiCheckCircleLight className="w-4 h-4" /> },
];

const ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv', 'json', 'txt', 'pdf', 'docx', 'md'];

const DOC_EXTENSIONS = ['zip', 'pdf', 'doc', 'docx', 'txt', 'xlsx', 'xls', 'csv', 'msg', 'ppt', 'pptx'];

/** The example tree in the documents step, as data rather than seven <p>s. */
const FOLDER_EXAMPLE: { depth: 0 | 1 | 2; name: string; kind: 'folder' | 'file' }[] = [
  { depth: 0, name: 'ClientDocuments/', kind: 'folder' },
  { depth: 1, name: 'Ola Nordmann/', kind: 'folder' },
  { depth: 2, name: 'contract.pdf', kind: 'file' },
  { depth: 2, name: 'id_document.pdf', kind: 'file' },
  { depth: 1, name: 'Acme AS/', kind: 'folder' },
  { depth: 2, name: 'agreement.docx', kind: 'file' },
  { depth: 2, name: 'invoice.xlsx', kind: 'file' },
];

const INDENT_CLASS: Record<0 | 1 | 2, string> = { 0: '', 1: 'ms-4', 2: 'ms-8' };

export default function ImportClientsWizard({
  isOpen,
  onClose,
  onComplete,
}: ImportClientsWizardProps) {
  const { t } = useTranslation();

  // Wizard state
  const [step, setStep] = useState<ImportWizardStep>('upload');
  const [jobUuid, setJobUuid] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData | null>(null);
  const [confirmResult, setConfirmResult] = useState<ImportConfirmResponse | null>(null);
  const [docsResult, setDocsResult] = useState<ImportDocumentsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [docMapping, setDocMapping] = useState<Record<string, string>>({});

  // PubSub hook
  const { progress, reset: resetProgress } = useImportPubSub({
    correlationId,
    enabled: step === 'parsing',
    onPreviewReady: async (uuid) => {
      if (uuid || jobUuid) {
        await loadPreview(uuid || jobUuid!);
        setStep('preview');
      }
    },
    onFailed: (err) => {
      setError(err);
      setStep('upload');
    },
  });

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setJobUuid(null);
      setCorrelationId(null);
      setSelectedFile(null);
      setPreviewData(null);
      setConfirmResult(null);
      setDocsResult(null);
      setIsLoading(false);
      setError(null);
      setEditingRow(null);
      setDocFiles([]);
      setDocMapping({});
      resetProgress();
    }
  }, [isOpen, resetProgress]);

  // ── Upload ──
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await uploadClientsImport(selectedFile);
      if (response.success && response.job_uuid) {
        setJobUuid(response.job_uuid);
        setCorrelationId(response.correlation_id || null);
        setStep('parsing');
      } else {
        const e = response.error;
        setError(toErrorText(e) || t('Upload failed'));
      }
    } catch (err: any) {
      const errorMsg = readErrorMessage(err, t('Upload failed'));
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, t]);

  // ── Preview ──
  const loadPreview = useCallback(async (uuid: string) => {
    try {
      const response = await getImportStatus(uuid);
      if (response.success && response.preview) {
        const rows = response.preview.rows as ImportClientRow[];
        rows.forEach((row) => {
          row._selected = !row.is_duplicate;
        });
        setPreviewData({ ...response.preview, rows });
      }
    } catch {
      setError(t('Failed to load preview'));
    }
  }, [t]);

  // ── Preview editing ──
  const rows = previewData?.rows as ImportClientRow[] | undefined;
  const selectedCount = rows?.filter((r) => r._selected).length || 0;
  const duplicateCount = rows?.filter((r) => r.is_duplicate).length || 0;
  const warningCount = rows?.filter((r) => r.unmatched_lawyers && r.unmatched_lawyers.length > 0).length || 0;

  const toggleRow = useCallback(
    (idx: number) => {
      if (!previewData) return;
      const r = [...previewData.rows] as ImportClientRow[];
      r[idx] = { ...r[idx], _selected: !r[idx]._selected };
      setPreviewData({ ...previewData, rows: r });
    },
    [previewData]
  );

  const toggleAll = useCallback(
    (selected: boolean) => {
      if (!previewData) return;
      const r = (previewData.rows as ImportClientRow[]).map((row) => ({
        ...row,
        _selected: row.is_duplicate ? false : selected,
      }));
      setPreviewData({ ...previewData, rows: r });
    },
    [previewData]
  );

  const updateRow = useCallback(
    (idx: number, updates: Partial<ImportClientRow>) => {
      if (!previewData) return;
      const r = [...previewData.rows] as ImportClientRow[];
      r[idx] = { ...r[idx], ...updates, _edited: true };
      setPreviewData({ ...previewData, rows: r });
    },
    [previewData]
  );

  const removeRow = useCallback(
    (idx: number) => {
      if (!previewData) return;
      const r = [...previewData.rows] as ImportClientRow[];
      r.splice(idx, 1);
      setPreviewData({ ...previewData, rows: r });
    },
    [previewData]
  );

  // ── Confirm ──
  const handleConfirm = useCallback(async () => {
    if (!jobUuid || !previewData) return;
    const selectedRows = (previewData.rows as ImportClientRow[]).filter((r) => r._selected);
    if (selectedRows.length === 0) {
      setError(t('No rows selected'));
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await updateImportPreview(jobUuid, selectedRows, { total_rows: selectedRows.length });
      setStep('confirm');
      const result = await confirmImport(jobUuid);
      setConfirmResult(result);

      // If clients were created, go to documents step
      if (result.created_count > 0) {
        setStep('documents');
      } else {
        setStep('results');
      }
    } catch (err: any) {
      const errorMsg = readErrorMessage(err, t('Import failed'));
      setError(errorMsg);
      setStep('preview');
    } finally {
      setIsLoading(false);
    }
  }, [jobUuid, previewData, t]);

  // ── Documents upload ──
  const handleDocFilesChange = useCallback((files: File[]) => {
    // Extract client folder mapping from webkitRelativePath if available (folder uploads)
    const mapping: Record<string, string> = {};
    for (const file of files) {
      const relPath = (file as any).webkitRelativePath || '';
      if (relPath) {
        const parts = relPath.split('/').filter(Boolean);
        // parts: [RootFolder, ClientName, subfolder?, filename]
        if (parts.length >= 2) {
          const clientFolder = parts.length >= 3 ? parts[1] : parts[0];
          mapping[file.name] = clientFolder;
        }
      }
    }
    setDocFiles(files);
    setDocMapping(Object.keys(mapping).length > 0 ? mapping : {});
  }, []);

  const handleDocUpload = useCallback(async () => {
    if (!jobUuid || docFiles.length === 0) return;
    setIsLoading(true);
    setError(null);
    try {
      const isZip = docFiles.length === 1 && docFiles[0].name.endsWith('.zip');
      const mappingToSend = Object.keys(docMapping).length > 0 ? docMapping : undefined;
      const result = await uploadImportDocuments(jobUuid, docFiles, mappingToSend, isZip);
      // Filter out macOS junk from failure list (.__MACOSX, ._files, .DS_Store)
      if (result.failed && result.failed.length > 0) {
        const isMacJunk = (f: string) =>
          f.includes('__MACOSX') || f.includes('.DS_Store') || f.split('/').some((p) => p.startsWith('._'));
        const realFailures = result.failed.filter((item: any) => !isMacJunk(item.file || ''));
        result.failed = realFailures;
        result.failed_count = realFailures.length;
      }
      setDocsResult(result);
      setStep('results');
    } catch (err: any) {
      const errorMsg = readErrorMessage(err, t('Document upload failed'));
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [jobUuid, docFiles, docMapping, t]);

  const handleSkipDocuments = useCallback(async () => {
    if (!jobUuid) return;
    try {
      await skipDocumentUpload(jobUuid);
    } catch {
      /* ignore */
    }
    setStep('results');
  }, [jobUuid]);

  const handleClose = useCallback(() => {
    if (confirmResult && confirmResult.created_count > 0) {
      onComplete?.();
    }
    onClose();
  }, [confirmResult, onComplete, onClose]);

  // ── Derived ──
  const createdCount = confirmResult?.created_count || 0;
  const resultStats: ImportStat[] = [
    { value: createdCount, label: t('Contacts Created'), tone: 'success' },
    { value: confirmResult?.failed_count || 0, label: t('Failed'), tone: 'danger' },
    ...(docsResult
      ? [{
          value: docsResult.uploaded,
          label: t('Documents Uploaded'),
          tone: 'info' as const,
          hint: docsResult.failed_count > 0
            ? t('{{count}} failed', { count: docsResult.failed_count })
            : undefined,
        }]
      : []),
  ];

  // ── Footer ──
  const footerLeading =
    step === 'preview' ? (
      <span className="text-sm text-fg-muted">
        {t('{{count}} contacts will be created', { count: selectedCount })}
      </span>
    ) : null;

  const footerActions = (
    <>
      {(step === 'upload' || step === 'results') && (
        <Button variant="secondary" size="small" onClick={handleClose}>
          {step === 'results' ? t('Done') : t('Cancel')}
        </Button>
      )}
      {step === 'upload' && (
        <Button
          variant="primary"
          size="small"
          onClick={handleUpload}
          disabled={!selectedFile || isLoading}
          isLoading={isLoading}
        >
          {t('Upload & Parse')}
        </Button>
      )}
      {step === 'preview' && (
        <>
          <Button
            variant="secondary"
            size="small"
            onClick={() => {
              setStep('upload');
              setPreviewData(null);
            }}
          >
            {t('Back')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleConfirm}
            disabled={selectedCount === 0 || isLoading}
            isLoading={isLoading}
          >
            {t('Import {{count}} Contacts', { count: selectedCount })}
          </Button>
        </>
      )}
      {step === 'documents' && (
        <>
          <Button variant="secondary" size="small" onClick={handleSkipDocuments}>
            {t('Skip')}
            <PiArrowRightLight className="w-4 h-4 ms-1 rtl:-scale-x-100" />
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleDocUpload}
            disabled={docFiles.length === 0 || isLoading}
            isLoading={isLoading}
            icon={<PiUploadLight className="w-4 h-4" />}
          >
            {t('Upload Documents')}
          </Button>
        </>
      )}
    </>
  );

  return (
    <ImportWizardShell
      isOpen={isOpen}
      icon={<PiUsersFourLight />}
      title={t('Import Contacts')}
      steps={STEPS}
      currentStep={step}
      error={error}
      onDismissError={() => setError(null)}
      onClose={handleClose}
      footerLeading={footerLeading}
      footerActions={footerActions}
    >
      {/* Step: Upload */}
      {step === 'upload' && (
        <div className="mx-auto flex w-full max-w-md flex-col items-center gap-6">
          <div className="text-center">
            <h3 className="text-sm font-medium text-fg-body">
              {t('Upload a file with contact data')}
            </h3>
            <p className="mt-1 text-sm text-fg-muted">
              {t(
                'Upload an Excel, CSV, JSON, or any text file with your client list. AI will parse and classify clients automatically.'
              )}
            </p>
          </div>

          <Button
            variant="secondary"
            size="small"
            onClick={() => downloadClientTemplate()}
            icon={<PiDownloadLight className="w-4 h-4" />}
          >
            {t('Download Excel Template')}
          </Button>

          <div className="w-full">
            <FileUploader
              files={selectedFile ? [selectedFile] : []}
              onFilesChange={(files) => {
                setSelectedFile(files[0] || null);
                setError(null);
              }}
              allowedExtensions={ALLOWED_EXTENSIONS}
              maxFiles={1}
            />
          </div>
        </div>
      )}

      {/* Step: Parsing - the one AI moment, so the one logo loader. */}
      {step === 'parsing' && (
        <ImportProcessingState
          ai
          title={t('Parsing and classifying your contacts...')}
          detail={progress.message || t('This usually takes 15-45 seconds')}
        />
      )}

      {/* Step: Preview */}
      {step === 'preview' && rows && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-fg-body">
                {t('Total')}: <strong className="tabular-nums">{rows.length}</strong>
              </span>
              <span className="text-success-600 dark:text-success-400">
                {t('Selected')}: <strong className="tabular-nums">{selectedCount}</strong>
              </span>
              {duplicateCount > 0 && (
                <span className="text-warning-600 dark:text-warning-400">
                  {t('Duplicates')}: <strong className="tabular-nums">{duplicateCount}</strong>
                </span>
              )}
              {warningCount > 0 && (
                <span className="text-warning-600 dark:text-warning-400">
                  {t('Unmatched lawyers')}: <strong className="tabular-nums">{warningCount}</strong>
                </span>
              )}
            </div>
            <ImportSelectionToggles
              onSelectAll={() => toggleAll(true)}
              onSelectNone={() => toggleAll(false)}
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken/70">
                <tr>
                  <th className={`${IMPORT_TH_CLASS} w-10`}>
                    <CheckBox
                      hideLabel
                      label={t('Select all rows')}
                      translate={false}
                      checked={selectedCount > 0 && selectedCount === rows.filter((r) => !r.is_duplicate).length}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  <th className={IMPORT_TH_CLASS}>{t('Name')}</th>
                  <th className={IMPORT_TH_CLASS}>{t('Type')}</th>
                  <th className={IMPORT_TH_CLASS}>{t('Email')}</th>
                  <th className={IMPORT_TH_CLASS}>{t('Phone')}</th>
                  <th className={IMPORT_TH_CLASS}>{t('Lawyers')}</th>
                  <th className={`${IMPORT_TH_CLASS} w-20`}>{t('Status')}</th>
                  <th className={`${IMPORT_TH_CLASS} w-16`} />
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline-soft">
                {rows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={importRowClass({
                      duplicate: !!row.is_duplicate,
                      selected: !!row._selected,
                    })}
                  >
                    <td className="px-3 py-2">
                      <CheckBox
                        hideLabel
                        label={t('Select {{name}}', { name: row.name })}
                        translate={false}
                        checked={!!row._selected}
                        disabled={!!row.is_duplicate}
                        onChange={() => toggleRow(idx)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {editingRow === idx ? (
                        <Input
                          type="text"
                          value={row.name}
                          onChange={(e) => updateRow(idx, { name: e.target.value })}
                        />
                      ) : (
                        <span className="font-medium text-fg-strong">{row.name}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge color={row.is_individual ? 'blue' : 'gray'}>
                        {row.client_type_name || row.client_type_code?.replace(/_/g, ' ') || '—'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {editingRow === idx ? (
                        <Input
                          type="email"
                          value={row.contact_email}
                          onChange={(e) => updateRow(idx, { contact_email: e.target.value })}
                        />
                      ) : (
                        <span className="text-xs text-fg-body">{row.contact_email}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-fg-muted">
                      {row.contact_phone || '—'}
                    </td>
                    <td className="px-3 py-2">
                      {row.matched_lawyers && row.matched_lawyers.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {row.matched_lawyers.map((l, i) => (
                            <Badge key={i} color="green">{l.name}</Badge>
                          ))}
                        </div>
                      )}
                      {row.unmatched_lawyers && row.unmatched_lawyers.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {row.unmatched_lawyers.map((name, i) => (
                            <Badge
                              key={i}
                              color="yellow"
                              icon={<PiWarningCircleLight className="w-3 h-3" />}
                              tooltip={t('Unmatched — assign after import')}
                            >
                              {name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {(!row.matched_lawyers || row.matched_lawyers.length === 0) &&
                        (!row.unmatched_lawyers || row.unmatched_lawyers.length === 0) && (
                          <span className="text-xs text-fg-muted">—</span>
                        )}
                    </td>
                    <td className="px-3 py-2">
                      {row.is_duplicate ? (
                        <Badge color="yellow">{t('Dup')}</Badge>
                      ) : row.warning ? (
                        <Badge
                          color="yellow"
                          tooltip={row.warning}
                          icon={<PiWarningCircleLight className="w-3.5 h-3.5" />}
                        >
                          {t('Warning')}
                        </Badge>
                      ) : (
                        <Badge
                          color="green"
                          icon={<PiCheckCircleLight className="w-3.5 h-3.5" />}
                        >
                          {t('Ready')}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <ImportRowActions
                        editing={editingRow === idx}
                        onEdit={() => setEditingRow(editingRow === idx ? null : idx)}
                        onRemove={() => removeRow(idx)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step: Confirm - ordinary work, so the plain spinner. */}
      {step === 'confirm' && (
        <ImportProcessingState
          title={t('Creating contacts & assigning lawyers...')}
          detail={t('Please do not close this window.')}
        />
      )}

      {/* Step: Documents */}
      {step === 'documents' && (
        <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6">
          <div className="text-center">
            <PiFilesLight className="mx-auto mb-3 h-10 w-10 text-fg-muted" />
            <h3 className="text-sm font-medium text-fg-body">
              {t('Upload Contact Documents')}{' '}
              <span className="font-normal text-fg-muted">({t('Optional')})</span>
            </h3>
            <p className="mt-2 text-sm text-fg-muted">
              {t(
                'Attach documents to the clients you just imported. You can upload a single ZIP file, or use the folder picker to select a folder.'
              )}
            </p>
          </div>

          {createdCount > 0 && (
            <Alert
              type="success"
              message={t('{{count}} contacts created successfully', { count: createdCount })}
            />
          )}

          {/* Expected folder structure */}
          <Well className="w-full">
            <div className="mb-2 flex items-center gap-2">
              <PiFolderOpenLight className="h-4 w-4 text-info-500" />
              <span className={FIELD_LABEL_CLASS}>{t('Expected folder structure')}</span>
            </div>
            <div className="ms-2 space-y-1 font-mono text-xs text-fg-muted">
              {FOLDER_EXAMPLE.map((entry) => (
                <p key={entry.name} className={`flex items-center gap-1 ${INDENT_CLASS[entry.depth]}`}>
                  {entry.kind === 'folder' ? (
                    <PiFolderLight className="h-3.5 w-3.5 flex-shrink-0 text-info-500" />
                  ) : (
                    <PiFileLight className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted" />
                  )}
                  {entry.name}
                </p>
              ))}
            </div>
            <p className="mt-2 text-xs text-fg-muted">
              {t('Each subfolder name must match the contact name exactly. Files inside will be attached to the matching contact.')}
            </p>
          </Well>

          <div className="w-full">
            <FileUploader
              files={docFiles}
              onFilesChange={handleDocFilesChange}
              allowedExtensions={DOC_EXTENSIONS}
            />
          </div>

          {docFiles.length > 0 && Object.keys(docMapping).length > 0 && (
            <Alert
              type="info"
              message={t('Folder structure detected — documents will be matched to contacts by folder name.')}
            />
          )}
        </div>
      )}

      {/* Step: Results */}
      {step === 'results' && (
        <div className="space-y-6">
          <ImportResultHeader
            succeeded={createdCount > 0}
            title={createdCount > 0 ? t('Import Completed') : t('Import Failed')}
            detail={t('{{created}} of {{total}} contacts imported', {
              created: createdCount,
              total: createdCount + (confirmResult?.failed_count || 0),
            })}
          />

          <ImportStatStrip stats={resultStats} />

          {docsResult && docsResult.failed && docsResult.failed.length > 0 && (
            <ImportFailureList
              tone="warning"
              title={t('Document Upload Failures')}
              items={docsResult.failed
                .slice(0, 10)
                .map((item: any) => `${item.file}: ${item.error}`)}
              overflowNote={
                docsResult.failed.length > 10
                  ? t('...and {{count}} more', { count: docsResult.failed.length - 10 })
                  : undefined
              }
            />
          )}

          {confirmResult?.failed_items && confirmResult.failed_items.length > 0 && (
            <ImportFailureList
              title={t('Failed Items')}
              items={confirmResult.failed_items.map((item) =>
                t('Row {{row}}{{who}}: {{error}}', {
                  row: item.row + 1,
                  who: item.name ? ` (${item.name})` : '',
                  error: item.error,
                })
              )}
            />
          )}
        </div>
      )}
    </ImportWizardShell>
  );
}
